<template>
  <div class="legalization-section">
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else-if="card">
      <!-- Pasek stanu — pierwsza rzecz, którą widzi pracownik -->
      <v-alert
        :type="typAlertu"
        text
        dense
        class="mb-4"
        :icon="ikonaStanu"
      >
        <div class="font-weight-medium">{{ etykietaStatusu }}</div>
        <div class="caption">{{ card.status.opis }}</div>
      </v-alert>

      <v-row dense>
        <!-- Kto -->
        <v-col cols="12" md="4">
          <v-card outlined class="rounded-lg fill-height">
            <v-card-title class="subtitle-1">
              <v-icon left small>mdi-account</v-icon>Osoba
            </v-card-title>
            <v-card-text>
              <div class="lg-row">
                <span class="lg-label">Obywatelstwo</span>
                <span class="lg-value">
                  <template v-if="card.citizenship.name">{{ card.citizenship.name }}</template>
                  <span v-else class="orange--text">nieustalone</span>
                  <v-chip
                    v-if="card.citizenship.wyliczonyZTekstu"
                    x-small outlined color="orange" class="ml-2"
                  >
                    z rejestracji: „{{ card.driver.citizenshipText }}" — zapisz, żeby potwierdzić
                  </v-chip>
                </span>
              </div>
              <div class="lg-row" v-if="profil.secondCitizenshipName">
                <span class="lg-label">Drugie obywatelstwo</span>
                <span class="lg-value">{{ profil.secondCitizenshipName }}</span>
              </div>
              <div class="lg-row">
                <span class="lg-label">Data urodzenia</span>
                <span class="lg-value">{{ data(card.driver.dateOfBirth) }}</span>
              </div>
              <div class="lg-row">
                <span class="lg-label">Miejsce urodzenia</span>
                <span class="lg-value">{{ profil.birthPlace || '—' }}</span>
              </div>
              <div class="lg-row">
                <span class="lg-label">Płeć</span>
                <span class="lg-value">{{ plec(profil.gender) }}</span>
              </div>
              <div class="lg-row">
                <span class="lg-label">Forma współpracy</span>
                <span class="lg-value">{{ card.driver.cooperationType || '—' }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Pobyt -->
        <v-col cols="12" md="4">
          <v-card outlined class="rounded-lg fill-height">
            <v-card-title class="subtitle-1">
              <v-icon left small>mdi-card-account-details-outline</v-icon>Pobyt
            </v-card-title>
            <v-card-text>
              <div class="lg-row">
                <span class="lg-label">Dokument</span>
                <span class="lg-value">{{ nazwaTypu(profil.residenceDocType, 'residenceDocTypes') }}</span>
              </div>
              <div class="lg-row">
                <span class="lg-label">Numer</span>
                <span class="lg-value">{{ profil.residenceDocNumber || '—' }}</span>
              </div>
              <div class="lg-row">
                <span class="lg-label">Ważny do</span>
                <span class="lg-value">
                  {{ data(profil.residenceDocExpiresAt) }}
                  <span class="caption" :class="klasaDni(card.status.pobyt.dniDoKonca)">
                    {{ opisDni(card.status.pobyt.dniDoKonca) }}
                  </span>
                </span>
              </div>
              <div class="lg-row">
                <span class="lg-label">Podstawa</span>
                <span class="lg-value">{{ profil.residenceBasis || '—' }}</span>
              </div>
              <div class="lg-row">
                <span class="lg-label">Paszport</span>
                <span class="lg-value">
                  {{ profil.passportNumber || '—' }}
                  <span v-if="profil.passportExpiresAt" class="caption grey--text">
                    (do {{ data(profil.passportExpiresAt) }})
                  </span>
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Praca -->
        <v-col cols="12" md="4">
          <v-card outlined class="rounded-lg fill-height">
            <v-card-title class="subtitle-1">
              <v-icon left small>mdi-briefcase-outline</v-icon>Praca
            </v-card-title>
            <v-card-text>
              <div class="lg-row">
                <span class="lg-label">Podstawa</span>
                <span class="lg-value">{{ nazwaTypu(profil.workPermitType, 'workPermitTypes') }}</span>
              </div>
              <div class="lg-row">
                <span class="lg-label">Numer</span>
                <span class="lg-value">{{ profil.workPermitNumber || '—' }}</span>
              </div>
              <div class="lg-row">
                <span class="lg-label">Ważne do</span>
                <span class="lg-value">
                  {{ data(profil.workPermitValidTo) }}
                  <span class="caption" :class="klasaDni(card.status.praca.dniDoKonca)">
                    {{ opisDni(card.status.praca.dniDoKonca) }}
                  </span>
                </span>
              </div>
              <div class="lg-row">
                <span class="lg-label">Prawo do pracy</span>
                <span class="lg-value">
                  <v-chip x-small :color="kolorPrawaDoPracy" text-color="white">
                    {{ prawoDoPracy }}
                  </v-chip>
                </span>
              </div>
              <div class="lg-row">
                <span class="lg-label">Urząd / województwo</span>
                <span class="lg-value">{{ profil.voivodeship || '—' }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Braki -->
      <v-alert
        v-if="card.status.braki && card.status.braki.length"
        type="warning"
        text dense
        class="mt-4"
      >
        <span class="font-weight-medium">Do uzupełnienia:</span>
        {{ card.status.braki.join(', ') }}
      </v-alert>

      <div class="d-flex align-center mt-4 mb-6">
        <v-btn color="primary" :disabled="!canEdit" @click="otworzEdycje">
          <v-icon left>mdi-pencil</v-icon>Uzupełnij dane pobytowe
        </v-btn>
        <v-btn
          class="ml-2"
          color="success"
          outlined
          :disabled="!canEdit || !card.profile"
          :loading="confirming"
          @click="potwierdz"
        >
          <v-icon left>mdi-check-decagram</v-icon>
          {{ card.profile && card.profile.verifiedAt ? 'Potwierdź ponownie' : 'Potwierdź dane' }}
        </v-btn>
        <v-spacer />
        <span v-if="card.profile && card.profile.verifiedAt" class="caption grey--text">
          Dane sprawdzone: {{ data(card.profile.verifiedAt) }}
        </span>
        <span v-else class="caption orange--text">Dane niesprawdzone przez pracownika</span>
      </div>

      <!-- Sprawy kierowcy -->
      <v-card outlined class="rounded-lg mb-4">
        <v-card-title class="subtitle-1">
          <v-icon left small>mdi-folder-account-outline</v-icon>Sprawy legalizacyjne
          <v-spacer />
          <v-btn small color="primary" :disabled="!canEdit" @click="dialogNowejSprawy = true">
            <v-icon left small>mdi-plus</v-icon>Utwórz sprawę
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p v-if="!sprawy.length" class="grey--text mb-0">
            Brak spraw. Historia postępowań zostaje tu na stałe — stare sprawy nie są nadpisywane.
          </p>
          <v-simple-table v-else dense>
            <thead>
              <tr>
                <th>Rodzaj sprawy</th>
                <th>Status</th>
                <th>Kompletność</th>
                <th>Utworzona</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in sprawy" :key="s.id" class="lg-clickable" @click="otworzSprawe(s.id)">
                <td>{{ s.proceduraNazwa }}</td>
                <td>
                  <v-chip x-small :color="kolorSprawy(s.status)" text-color="white">
                    {{ s.statusNazwa }}
                  </v-chip>
                </td>
                <td>
                  {{ s.kompletnosc.procent }}%
                  <span v-if="s.ostrzezenia && s.ostrzezenia.length" class="caption orange--text">
                    ({{ s.ostrzezenia.length }} ostrzeżeń)
                  </span>
                </td>
                <td>{{ data(s.createdAt) }}</td>
                <td><v-icon small color="grey lighten-1">mdi-chevron-right</v-icon></td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <!-- Dokumenty sprawy -->
      <v-card outlined class="rounded-lg mb-6">
        <v-card-title class="subtitle-1">
          <v-icon left small>mdi-folder-outline</v-icon>Dokumenty legalizacyjne w systemie
        </v-card-title>
        <v-card-text>
          <p v-if="!card.documents.length" class="grey--text mb-0">
            Brak wgranych dokumentów. Dodaje się je w zakładce „Dokumenty" — typy legalizacyjne
            są na tej samej liście co pozostałe dokumenty kierowcy.
          </p>
          <v-simple-table v-else dense>
            <thead>
              <tr>
                <th>Dokument</th>
                <th>Ważny do</th>
                <th>Stan</th>
                <th>Wgrano</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in card.documents" :key="d.id">
                <td>{{ d.label }}</td>
                <td>{{ data(d.expiryDate) }}</td>
                <td>
                  <v-chip x-small :color="kolorDokumentu(d.status)" text-color="white">
                    {{ etykietaDokumentu(d.status) }}
                  </v-chip>
                  <span v-if="d.rejectionReason" class="caption error--text ml-2">
                    {{ d.rejectionReason }}
                  </span>
                </td>
                <td>{{ data(d.updatedAt) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <p class="caption grey--text">
        System pokazuje wyłącznie to, co wynika z wpisanych dat. Ocena, czy dokumentacja spełnia
        wymogi urzędu, należy do pracownika.
      </p>

      <!-- Nowa sprawa -->
      <v-dialog v-model="dialogNowejSprawy" max-width="560">
        <v-card>
          <v-card-title class="subtitle-1">Nowa sprawa — {{ card.driver.firstName }} {{ card.driver.lastName }}</v-card-title>
          <v-card-text>
            <v-select
              v-model="nowaSprawa.procedura"
              :items="procedury"
              item-text="nazwa"
              item-value="kod"
              label="Rodzaj sprawy"
              outlined dense hide-details class="mb-3"
            />
            <v-select
              v-model="nowaSprawa.wojewodztwo"
              :items="slowniki.voivodeships || []"
              label="Województwo / urząd"
              outlined dense hide-details clearable class="mb-3"
            />
            <div class="section-divider">Podstawa pracy</div>
            <v-radio-group v-model="nowaSprawa.pracodawcaBTP" class="mt-1" hide-details>
              <v-radio label="BTP zatrudnia tę osobę" :value="true" />
              <v-radio label="Kierowca prowadzi własną działalność" :value="false" />
              <v-radio label="Nie wiem — niech system o to zapyta" :value="null" />
            </v-radio-group>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="tworzenie" @click="dialogNowejSprawy = false">Anuluj</v-btn>
            <v-btn color="primary" :loading="tworzenie" @click="utworzSprawe">Utwórz</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <legalization-case-dialog
        v-model="dialogSprawy"
        :case-id="otwartaSprawa"
        :can-edit="canEdit"
        @zmieniono="pobierzSprawy"
      />

      <!-- Edycja -->
      <v-dialog v-model="dialog" max-width="900" persistent scrollable>
        <v-card>
          <v-card-title>Dane pobytowe — {{ card.driver.firstName }} {{ card.driver.lastName }}</v-card-title>
          <v-card-text>
            <p class="caption grey--text">
              Puste pole oznacza „wyczyść". Numery dokumentów zapisujemy zaszyfrowane.
            </p>
            <v-row dense>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="form.citizenship"
                  :items="opcjeKrajow"
                  item-text="namePl"
                  item-value="code"
                  label="Obywatelstwo"
                  outlined dense hide-details clearable
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="form.secondCitizenship"
                  :items="opcjeKrajow"
                  item-text="namePl"
                  item-value="code"
                  label="Drugie obywatelstwo"
                  outlined dense hide-details clearable
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="form.gender"
                  :items="[{text:'Mężczyzna',value:'M'},{text:'Kobieta',value:'F'},{text:'Inna',value:'X'}]"
                  label="Płeć" outlined dense hide-details clearable
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="form.voivodeship"
                  :items="slowniki.voivodeships || []"
                  label="Województwo" outlined dense hide-details clearable
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field v-model="form.birthPlace" label="Miejsce urodzenia" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="form.birthCountry"
                  :items="opcjeKrajow" item-text="namePl" item-value="code"
                  label="Kraj urodzenia" outlined dense hide-details clearable
                />
              </v-col>

              <v-col cols="12"><div class="section-divider">Paszport</div></v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="form.passportNumber" label="Numer paszportu" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="3">
                <v-autocomplete
                  v-model="form.passportCountry" :items="opcjeKrajow"
                  item-text="namePl" item-value="code"
                  label="Kraj wydania" outlined dense hide-details clearable
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="form.passportIssuedAt" type="date" label="Data wydania" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="form.passportExpiresAt" type="date" label="Ważny do" outlined dense hide-details />
              </v-col>

              <v-col cols="12"><div class="section-divider">Tytuł pobytowy</div></v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="form.residenceDocType"
                  :items="slowniki.residenceDocTypes || []"
                  item-text="nazwa" item-value="kod"
                  label="Rodzaj dokumentu" outlined dense hide-details clearable
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="form.residenceDocNumber" label="Numer dokumentu" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="form.residenceDocIssuedAt" type="date" label="Data wydania" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="form.residenceDocExpiresAt" type="date" label="Ważny do" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.residenceDocIssuedBy" label="Organ wydający" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.residenceBasis" label="Podstawa pobytu" outlined dense hide-details />
              </v-col>

              <v-col cols="12"><div class="section-divider">Prawo do pracy</div></v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="form.workPermitType"
                  :items="slowniki.workPermitTypes || []"
                  item-text="nazwa" item-value="kod"
                  label="Podstawa" outlined dense hide-details clearable
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="form.workPermitNumber" label="Numer" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="form.workPermitValidFrom" type="date" label="Od" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="form.workPermitValidTo" type="date" label="Do" outlined dense hide-details />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="form.workRightStatus"
                  :items="[
                    {text:'Do weryfikacji',value:'DO_WERYFIKACJI'},
                    {text:'Ma prawo do pracy',value:'TAK'},
                    {text:'Nie ma prawa do pracy',value:'NIE'}
                  ]"
                  label="Ocena pracownika" outlined dense hide-details
                />
              </v-col>
              <v-col cols="12" md="8">
                <v-text-field v-model="form.workRightNote" label="Uwaga do prawa pracy" outlined dense hide-details />
              </v-col>

              <v-col cols="12">
                <v-textarea v-model="form.note" label="Notatka biura" outlined dense rows="2" hide-details />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="saving" @click="dialog = false">Anuluj</v-btn>
            <v-btn color="primary" :loading="saving" @click="zapisz">Zapisz</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

    <v-alert v-else type="info" text dense>
      Nie udało się wczytać danych legalizacji.
    </v-alert>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import LegalizationCaseDialog from "@/components/LegalizationCaseDialog.vue";

export default {
  name: "LegalizationSection",
  components: { LegalizationCaseDialog },
  props: {
    driverId: { type: String, default: null },
    canEdit: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: false,
      saving: false,
      confirming: false,
      dialog: false,
      card: null,
      slowniki: {},
      form: {},
      sprawy: [],
      procedury: [],
      dialogNowejSprawy: false,
      dialogSprawy: false,
      otwartaSprawa: null,
      tworzenie: false,
      nowaSprawa: { procedura: "POBYT_I_PRACA", wojewodztwo: null, pracodawcaBTP: null },
    };
  },
  computed: {
    profil() {
      return (this.card && this.card.profile) || {};
    },
    opcjeKrajow() {
      return this.slowniki.countries || [];
    },
    etykietaStatusu() {
      return (
        {
          NIE_DOTYCZY: "Legalizacja nie dotyczy tej osoby",
          WYMAGA_DANYCH: "Brakuje danych",
          PO_TERMINIE: "Po terminie",
          KONCZY_SIE: "Termin się zbliża",
          AKTUALNY: "Daty aktualne",
        }[this.card.status.status] || this.card.status.status
      );
    },
    typAlertu() {
      return (
        {
          NIE_DOTYCZY: "success",
          WYMAGA_DANYCH: "info",
          PO_TERMINIE: "error",
          KONCZY_SIE: "warning",
          AKTUALNY: "success",
        }[this.card.status.status] || "info"
      );
    },
    ikonaStanu() {
      return (
        {
          NIE_DOTYCZY: "mdi-check-circle-outline",
          WYMAGA_DANYCH: "mdi-help-circle-outline",
          PO_TERMINIE: "mdi-alert-octagon",
          KONCZY_SIE: "mdi-clock-alert-outline",
          AKTUALNY: "mdi-check-circle-outline",
        }[this.card.status.status] || "mdi-information-outline"
      );
    },
    prawoDoPracy() {
      return (
        {
          TAK: "Ma prawo do pracy",
          NIE: "Brak prawa do pracy",
          DO_WERYFIKACJI: "Do weryfikacji",
        }[this.profil.workRightStatus] || "Do weryfikacji"
      );
    },
    kolorPrawaDoPracy() {
      return (
        { TAK: "success", NIE: "error", DO_WERYFIKACJI: "blue-grey" }[
          this.profil.workRightStatus
        ] || "blue-grey"
      );
    },
  },
  watch: {
    driverId: {
      immediate: true,
      handler(v) {
        if (v) this.fetch();
      },
    },
  },
  methods: {
    getConfig() {
      return {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      };
    },
    async fetch() {
      if (!this.driverId) return;
      this.loading = true;
      try {
        const [karta, slowniki] = await Promise.all([
          axios.get(
            process.env.VUE_APP_ROOT_API + "legalization/driver/" + this.driverId,
            this.getConfig()
          ),
          axios.get(
            process.env.VUE_APP_ROOT_API + "legalization/dictionaries",
            this.getConfig()
          ),
        ]);
        this.card = karta.data.data;
        this.slowniki = slowniki.data.data;
        await this.pobierzSprawy();
      } catch (e) {
        this.card = null;
        // 403 przy braku uprawnienia do strony — nie straszymy alertem.
        if (!e.response || e.response.status !== 403) {
          Swal.fire(
            "Błąd",
            (e.response && e.response.data && e.response.data.message) ||
              "Nie udało się pobrać danych legalizacji",
            "error"
          );
        }
      } finally {
        this.loading = false;
      }
    },
    async pobierzSprawy() {
      if (!this.driverId) return;
      try {
        const [sprawy, slowniki] = await Promise.all([
          axios.get(
            process.env.VUE_APP_ROOT_API + "legalization/cases?driverId=" + this.driverId,
            this.getConfig()
          ),
          axios.get(
            process.env.VUE_APP_ROOT_API + "legalization/case-dictionaries",
            this.getConfig()
          ),
        ]);
        this.sprawy = sprawy.data.data || [];
        this.procedury = slowniki.data.data.procedury || [];
      } catch (e) {
        // Sprawy to dodatek do karty — ich brak nie może zasłonić danych pobytowych.
      }
    },
    otworzSprawe(id) {
      this.otwartaSprawa = id;
      this.dialogSprawy = true;
    },
    async utworzSprawe() {
      this.tworzenie = true;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + "legalization/cases",
          { driverId: this.driverId, ...this.nowaSprawa },
          this.getConfig()
        );
        this.dialogNowejSprawy = false;
        await this.pobierzSprawy();
        this.otworzSprawe(res.data.data.id);
      } catch (e) {
        Swal.fire(
          "Nie utworzono sprawy",
          (e.response && e.response.data && e.response.data.message) || "",
          "error"
        );
      } finally {
        this.tworzenie = false;
      }
    },
    kolorSprawy(status) {
      if (["DECYZJA_NEGATYWNA", "WEZWANIE"].includes(status)) return "error";
      if (["ZWERYFIKOWANA", "DECYZJA_POZYTYWNA", "ZAKONCZONA"].includes(status)) return "success";
      if (["ZLOZONA", "OCZEKIWANIE_NA_URZAD"].includes(status)) return "blue";
      return "blue-grey";
    },
    otworzEdycje() {
      const p = this.card.profile || {};
      this.form = {
        citizenship: this.card.citizenship.code || null,
        secondCitizenship: p.secondCitizenshipCode || null,
        gender: p.gender || null,
        birthPlace: p.birthPlace || "",
        birthCountry: p.birthCountryCode || null,
        passportNumber: p.passportNumber || "",
        passportCountry: p.passportCountryCode || null,
        passportIssuedAt: this.doInputu(p.passportIssuedAt),
        passportExpiresAt: this.doInputu(p.passportExpiresAt),
        residenceDocType: p.residenceDocType || null,
        residenceDocNumber: p.residenceDocNumber || "",
        residenceDocIssuedBy: p.residenceDocIssuedBy || "",
        residenceDocIssuedAt: this.doInputu(p.residenceDocIssuedAt),
        residenceDocExpiresAt: this.doInputu(p.residenceDocExpiresAt),
        residenceBasis: p.residenceBasis || "",
        workPermitType: p.workPermitType || null,
        workPermitNumber: p.workPermitNumber || "",
        workPermitValidFrom: this.doInputu(p.workPermitValidFrom),
        workPermitValidTo: this.doInputu(p.workPermitValidTo),
        workRightStatus: p.workRightStatus || "DO_WERYFIKACJI",
        workRightNote: p.workRightNote || "",
        voivodeship: p.voivodeship || null,
        note: p.note || "",
      };
      this.dialog = true;
    },
    async zapisz() {
      this.saving = true;
      try {
        const res = await axios.put(
          process.env.VUE_APP_ROOT_API + "legalization/driver/" + this.driverId,
          this.form,
          this.getConfig()
        );
        this.card = res.data.data;
        this.dialog = false;
        Swal.fire({
          icon: "success",
          title: "Zapisano",
          timer: 1200,
          showConfirmButton: false,
        });
      } catch (e) {
        Swal.fire(
          "Nie zapisano",
          (e.response && e.response.data && e.response.data.message) ||
            "Błąd zapisu danych pobytowych",
          "error"
        );
      } finally {
        this.saving = false;
      }
    },
    async potwierdz() {
      const { isConfirmed } = await Swal.fire({
        icon: "question",
        title: "Potwierdzić dane?",
        text:
          "Potwierdzasz, że dane pobytowe zgadzają się z dokumentami, które widziałeś. " +
          "To nie jest orzeczenie o legalności pobytu.",
        showCancelButton: true,
        confirmButtonText: "Potwierdzam",
        cancelButtonText: "Anuluj",
      });
      if (!isConfirmed) return;
      this.confirming = true;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API +
            "legalization/driver/" +
            this.driverId +
            "/confirm",
          {},
          this.getConfig()
        );
        this.card = res.data.data;
      } catch (e) {
        Swal.fire(
          "Błąd",
          (e.response && e.response.data && e.response.data.message) ||
            "Nie udało się zapisać potwierdzenia",
          "error"
        );
      } finally {
        this.confirming = false;
      }
    },
    doInputu(v) {
      if (!v) return "";
      return new Date(v).toISOString().slice(0, 10);
    },
    data(v) {
      if (!v) return "—";
      return new Date(v).toLocaleDateString("pl-PL");
    },
    plec(v) {
      return { M: "Mężczyzna", F: "Kobieta", X: "Inna" }[v] || "—";
    },
    nazwaTypu(kod, slownik) {
      if (!kod) return "—";
      const lista = this.slowniki[slownik] || [];
      const poz = lista.find((x) => x.kod === kod);
      return poz ? poz.nazwa : kod;
    },
    opisDni(dni) {
      if (dni === null || dni === undefined) return "";
      if (dni < 0) return `(${Math.abs(dni)} dni po terminie)`;
      if (dni === 0) return "(dziś)";
      return `(za ${dni} dni)`;
    },
    klasaDni(dni) {
      if (dni === null || dni === undefined) return "grey--text";
      if (dni <= 30) return "error--text";
      if (dni <= 90) return "orange--text";
      return "grey--text";
    },
    kolorDokumentu(status) {
      return (
        { VERIFIED: "success", PENDING: "orange", REJECTED: "error" }[status] ||
        "grey"
      );
    },
    etykietaDokumentu(status) {
      return (
        {
          VERIFIED: "Zweryfikowany",
          PENDING: "Czeka na sprawdzenie",
          REJECTED: "Odrzucony",
        }[status] || status
      );
    },
  },
};
</script>

<style scoped>
.lg-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.lg-row:last-child {
  border-bottom: none;
}
.lg-label {
  color: #7a7f87;
  font-size: 13px;
  white-space: nowrap;
}
.lg-value {
  font-size: 13px;
  text-align: right;
  font-weight: 500;
}
.lg-clickable {
  cursor: pointer;
}
.section-divider {
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8f98;
  margin: 12px 0 4px;
}
</style>
